/**
 * CSK Bridge-Witness Lifecycle — witness kinds, Seam-Normal-Form phase traces,
 * receipts, and exactly-once consumption for bridge admission.
 *
 * A bridge witness is the constructive evidence that admits an arrow across a
 * seam. The CSK distinction that boolean flags and nullable structs lose is the
 * difference between "no bridge exists", "a witness is missing", "a witness
 * exists but is stale / revoked / already consumed", and "the bridge was
 * admitted and emitted an audit receipt". This module makes each of those
 * states a distinct, typed verdict.
 *
 * Theorem, generator, and neutralization-mode metadata are always derived from
 * `NeutralizationSecurityGuarantee` rather than copied as ad hoc strings, so a
 * witness can never drift from the theorem that admits it.
 *
 * Agda source: Foundation/Bridge/, Foundation/Linear/ (SNF B1 → B2 → B3 → B4)
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand, NeutralizationTheoremCode } from "./types.js";
import type { NeutralizationSecurityGuarantee } from "../shared/canonical.js";
import { getNeutralizationTheoremMeta } from "../shared/canonical.js";
// Type-only, intentionally cyclic: BridgeReceipt names the bridge it accounts
// for. `import type` is erased at runtime, so there is no module cycle.
import type { BridgeId } from "./seams.js";

// ── Identifiers ────────────────────────────────────────────────

/** A bridge-witness identifier — branded to prevent accidental substitution. */
export type BridgeWitnessId = Brand<string, "BridgeWitnessId">;

/** Construct a bridge-witness identifier. */
export function bridgeWitnessId(value: string): BridgeWitnessId {
  return value as BridgeWitnessId;
}

/** Who issued a witness (an authority, agent, or governance body). */
export type WitnessIssuer = string;

/** The scope a witness is valid within (a lane, domain, or capability range). */
export type WitnessScope = string;

/** An append-only audit anchor (a hash, ledger head, or receipt pointer). */
export type WitnessAnchor = string;

// ── Witness kinds ──────────────────────────────────────────────

/**
 * The semantic kinds of evidence that can admit a bridge, one per
 * Neutralization Theorem. Naming a witness kind ties it to the theorem whose
 * hom-set it re-inhabits.
 */
export const WITNESS_KINDS = [
  "provenance", // D1 — DATA.ORIGIN
  "declassification", // D2 — DATA.REACH
  "pre-authorization", // P — PERMIT
  "separation", // S — SEPARATE
  "precommitment", // W — PRECOMMIT
  "consumption", // R1 — RES.CONSUME
  "release", // R2 — RES.RELEASE
  "closure", // C — COMPOSE
  "establishment", // I1 — INV.ESTABLISH
  "preservation", // I2 — INV.PRESERVE
] as const;

/** A bridge-witness kind. */
export type WitnessKind = (typeof WITNESS_KINDS)[number];

/** The witness kind that re-inhabits the hom-set each theorem closes. */
export const WITNESS_KIND_BY_THEOREM = {
  D1: "provenance",
  D2: "declassification",
  P: "pre-authorization",
  S: "separation",
  W: "precommitment",
  R1: "consumption",
  R2: "release",
  C: "closure",
  I1: "establishment",
  I2: "preservation",
} as const satisfies Record<NeutralizationTheoremCode, WitnessKind>;

/** Derive the witness kind a theorem governs. */
export function witnessKindForTheorem(theorem: NeutralizationTheoremCode): WitnessKind {
  return WITNESS_KIND_BY_THEOREM[theorem];
}

// ── Seam Normal Form phases ────────────────────────────────────

/** The four Seam-Normal-Form phases: witness, authorize, execute, account. */
export const SNF_PHASES = ["B1", "B2", "B3", "B4"] as const;

/** A Seam-Normal-Form phase. */
export type SeamNormalFormPhase = (typeof SNF_PHASES)[number];

/** The role each SNF phase plays in the bridge lifecycle. */
export const SNF_PHASE_ROLE = {
  B1: "witness",
  B2: "authorize",
  B3: "execute",
  B4: "account",
} as const satisfies Record<SeamNormalFormPhase, string>;

const PHASE_ORDER: Record<SeamNormalFormPhase, number> = { B1: 0, B2: 1, B3: 2, B4: 3 };

/** The canonical complete SNF phase sequence for one admitted bridge. */
export type SeamNormalFormTracePhases = readonly ["B1", "B2", "B3", "B4"];

/**
 * A raw trace candidate accepted at trust boundaries. Candidates are narrowed to
 * {@link SeamNormalFormTrace} before a receipt can be emitted.
 */
export interface SeamNormalFormTraceCandidate {
  readonly phases: readonly unknown[];
}

/** A complete trace through the four SNF phases for one bridge admission. */
export interface SeamNormalFormTrace {
  readonly phases: SeamNormalFormTracePhases;
}

/** The unique complete SNF trace used when callers do not supply a trace. */
export const SEAM_NORMAL_FORM_TRACE = {
  phases: SNF_PHASES,
} as const satisfies SeamNormalFormTrace;

/**
 * The non-feedback invariant: only a B1 witness may seed a B2 authorization.
 * Accounting evidence (a B4 receipt) can never be fed back as authorization —
 * that is exactly the temporal-feedback attack PRECOMMIT neutralizes.
 */
export function canSeedAuthorization(phase: SeamNormalFormPhase): boolean {
  return phase === "B1";
}

/**
 * A trace is feedback-free when its phases never run backwards in time and
 * never re-enter authorization (B2) after accounting (B4) has been reached.
 */
export function isFeedbackFreeTrace(trace: SeamNormalFormTraceCandidate): boolean {
  let previous = -1;
  let accounted = false;
  for (const phase of trace.phases) {
    const order = typeof phase === "string" ? PHASE_ORDER[phase as SeamNormalFormPhase] : undefined;
    if (order === undefined) return false;
    if (order < previous) return false; // no time reversal
    if (accounted && phase === "B2") return false; // B4 receipt fed back as B2
    if (phase === "B4") accounted = true;
    previous = order;
  }
  return true;
}

/** True when a candidate is the complete B1 → B2 → B3 → B4 SNF trace. */
export function isSeamNormalFormTrace(
  trace: SeamNormalFormTraceCandidate,
): trace is SeamNormalFormTrace {
  return (
    trace.phases.length === SNF_PHASES.length &&
    SNF_PHASES.every((phase, index) => trace.phases[index] === phase)
  );
}

// ── Bridge witnesses ───────────────────────────────────────────

/**
 * A bridge witness: the constructive evidence that admits a bridge across a
 * seam, carrying its issuer, scope, lifetime, and the theorem guarantee it
 * discharges. Theorem-derived metadata lives on `guarantee`; `kind` is fixed by
 * that guarantee through {@link witnessKindForTheorem}.
 */
export interface BridgeWitness {
  readonly id: BridgeWitnessId;
  readonly kind: WitnessKind;
  readonly issuer: WitnessIssuer;
  readonly scope: WitnessScope;
  readonly guarantee: NeutralizationSecurityGuarantee;
  readonly issuedAt: number;
  /** Expiry timestamp, or `null` when the witness does not expire. */
  readonly expiresAt: number | null;
  /** Audit anchor the witness was minted against, or `null`. */
  readonly receiptAnchor: WitnessAnchor | null;
  readonly revoked: boolean;
}

/** Fields a caller supplies to mint a witness; theorem metadata is derived. */
export interface BridgeWitnessDefinition {
  readonly id: string;
  readonly theorem: NeutralizationTheoremCode;
  readonly issuer: WitnessIssuer;
  readonly scope: WitnessScope;
  readonly issuedAt: number;
  readonly expiresAt?: number | null;
  readonly receiptAnchor?: WitnessAnchor | null;
  readonly revoked?: boolean;
}

/**
 * Mint a bridge witness from a theorem code. The guarantee, generator, mode,
 * and witness kind are derived from the theorem metadata, so a witness can
 * never claim a kind its theorem does not govern.
 */
export function defineBridgeWitness(definition: BridgeWitnessDefinition): BridgeWitness {
  const guarantee = getNeutralizationTheoremMeta(definition.theorem);
  return {
    id: bridgeWitnessId(definition.id),
    kind: witnessKindForTheorem(definition.theorem),
    issuer: definition.issuer,
    scope: definition.scope,
    guarantee,
    issuedAt: definition.issuedAt,
    expiresAt: definition.expiresAt ?? null,
    receiptAnchor: definition.receiptAnchor ?? null,
    revoked: definition.revoked ?? false,
  };
}

// ── Linear (exactly-once / bounded-use) consumption ────────────

/** Whether a witness still has admissions left, or has been spent. */
export type LinearWitnessState = "available" | "consumed";

const AUTHENTIC_WITNESS_CONSUMPTIONS = new WeakSet();

/**
 * Consumption ledger for a witness with a bounded number of admissions. A
 * witness with `maxUses === 1` is exactly-once.
 *
 * TypeScript cannot express Rust-style move semantics, so the ledger carries
 * private mutable state and is authenticated at runtime. Retaining an old
 * reference observes the advanced state after consumption, and copied snapshots
 * are rejected by witness admission.
 */
export class WitnessConsumption {
  readonly #witnessId: BridgeWitnessId;
  readonly #maxUses: number;
  #uses: number;

  declare readonly witnessId: BridgeWitnessId;
  declare readonly state: LinearWitnessState;
  declare readonly maxUses: number;
  declare readonly uses: number;

  private constructor(witnessId: BridgeWitnessId, maxUses: number) {
    this.#witnessId = witnessId;
    this.#maxUses = maxUses;
    this.#uses = 0;
    Object.defineProperties(this, {
      witnessId: { enumerable: true, get: () => this.#witnessId },
      state: {
        enumerable: true,
        get: () => (this.#uses >= this.#maxUses ? "consumed" : "available"),
      },
      maxUses: { enumerable: true, get: () => this.#maxUses },
      uses: { enumerable: true, get: () => this.#uses },
    });
    AUTHENTIC_WITNESS_CONSUMPTIONS.add(this);
    Object.freeze(this);
  }

  static open(witnessId: BridgeWitnessId, maxUses: number): WitnessConsumption {
    return new WitnessConsumption(witnessId, maxUses);
  }

  consumeUse(): boolean {
    if (this.#uses >= this.#maxUses) return false;
    this.#uses += 1;
    return true;
  }
}

/** True only for ledgers minted by {@link openConsumption}. */
export function isWitnessConsumption(value: unknown): value is WitnessConsumption {
  return typeof value === "object" && value !== null && AUTHENTIC_WITNESS_CONSUMPTIONS.has(value);
}

/**
 * Open a consumption ledger for a witness with `maxUses` admissions.
 * `maxUses` must be a finite integer ≥ 1; invalid values throw a RangeError
 * so that NaN or fractional inputs cannot bypass the bounded-use guarantee.
 */
export function openConsumption(witnessId: BridgeWitnessId, maxUses = 1): WitnessConsumption {
  if (!Number.isFinite(maxUses) || !Number.isInteger(maxUses) || maxUses < 1) {
    throw new RangeError(
      `openConsumption: maxUses must be a finite integer ≥ 1, got ${String(maxUses)}`,
    );
  }
  return WitnessConsumption.open(witnessId, maxUses);
}

/**
 * Spend one admission against a consumption ledger. Returns the advanced
 * ledger and whether the admission was granted; an exhausted ledger admits
 * nothing further, making double-spend structurally inexpressible.
 */
export function consumeWitnessUse(consumption: WitnessConsumption): {
  readonly consumption: WitnessConsumption;
  readonly admitted: boolean;
} {
  if (!isWitnessConsumption(consumption)) {
    throw new TypeError(
      "consumeWitnessUse: consumption ledger must be opened with openConsumption",
    );
  }
  return { consumption, admitted: consumption.consumeUse() };
}

// ── Receipts ───────────────────────────────────────────────────

/**
 * An audit receipt emitted when a bridge is admitted. Receipts are freely
 * copyable evidence, but — like {@link AuthorityReceipt} — carry no method to
 * mint fresh authority, so replay is structurally inexpressible.
 */
export interface BridgeReceipt {
  readonly bridgeId: BridgeId;
  readonly witnessId: BridgeWitnessId;
  readonly guarantee: NeutralizationSecurityGuarantee;
  readonly consumedResources: readonly string[];
  readonly trace: SeamNormalFormTrace;
  readonly previousAnchor: WitnessAnchor | null;
  readonly nextAnchor: WitnessAnchor | null;
}

// ── Witness verdicts ───────────────────────────────────────────

/**
 * What is required of a witness for a particular bridge admission. The required
 * witness kind is not stored independently — it is derived from `theorem` (the
 * single source of truth) via {@link witnessKindForTheorem}, so a requirement
 * cannot express a kind that contradicts its theorem.
 */
export interface BridgeWitnessRequirement {
  readonly scope: WitnessScope;
  readonly theorem: NeutralizationTheoremCode;
}

/**
 * The distinct lifecycle states of a bridge witness. Unlike a boolean, this
 * separates "no witness" from "wrong witness" from "spent witness" from an
 * admitted bridge that emitted a receipt.
 */
export type BridgeWitnessVerdict =
  | { readonly status: "present"; readonly witness: BridgeWitness }
  | { readonly status: "missing"; readonly required: WitnessKind }
  | { readonly status: "stale"; readonly witness: BridgeWitness; readonly checkedAt: number }
  | { readonly status: "revoked"; readonly witness: BridgeWitness }
  | {
      readonly status: "scope-mismatch";
      readonly witness: BridgeWitness;
      readonly expected: WitnessScope;
    }
  | {
      readonly status: "theorem-mismatch";
      readonly witness: BridgeWitness;
      readonly expected: NeutralizationTheoremCode;
    }
  | { readonly status: "consumed"; readonly witness: BridgeWitness }
  | {
      readonly status: "invalid-trace";
      readonly witness: BridgeWitness;
      readonly trace: SeamNormalFormTraceCandidate;
    }
  | {
      readonly status: "admitted";
      readonly witness: BridgeWitness;
      readonly receipt: BridgeReceipt;
    };

/** Options threaded into witness evaluation. */
export interface EvaluateWitnessOptions {
  readonly now?: number;
  readonly consumption?: WitnessConsumption;
}

/**
 * Evaluate a witness against a requirement, returning the most specific
 * non-admitted verdict. An absent witness is `missing`; a present-and-valid
 * witness is `present`. Admission (and the receipt it emits) is a separate,
 * resource-consuming step — see {@link admitBridgeWitness}.
 */
export function evaluateBridgeWitness(
  requirement: BridgeWitnessRequirement,
  witness: BridgeWitness | null,
  options: EvaluateWitnessOptions = {},
): BridgeWitnessVerdict {
  // The required witness kind is derived from the theorem, the single source of
  // truth, so the `missing` verdict always reports the kind the theorem governs.
  const requiredKind = witnessKindForTheorem(requirement.theorem);
  if (witness === null) {
    return { status: "missing", required: requiredKind };
  }
  if (witness.revoked) {
    return { status: "revoked", witness };
  }
  const now = options.now ?? Date.now();
  if (witness.expiresAt !== null && witness.expiresAt <= now) {
    return { status: "stale", witness, checkedAt: now };
  }
  if (witness.scope !== requirement.scope) {
    return { status: "scope-mismatch", witness, expected: requirement.scope };
  }
  if (witness.guarantee.code !== requirement.theorem) {
    return { status: "theorem-mismatch", witness, expected: requirement.theorem };
  }
  // A consumption ledger only blocks the witness it was opened against — a
  // ledger for a different witness id is irrelevant here.
  if (options.consumption?.witnessId === witness.id) {
    if (!isWitnessConsumption(options.consumption) || options.consumption.state === "consumed") {
      return { status: "consumed", witness };
    }
  }
  return { status: "present", witness };
}

/** Inputs needed to admit a bridge once its witness has passed evaluation. */
export interface AdmissionRequest {
  readonly bridgeId: BridgeId;
  readonly consumedResources?: readonly string[];
  readonly trace?: SeamNormalFormTraceCandidate;
  readonly previousAnchor?: WitnessAnchor | null;
  readonly nextAnchor?: WitnessAnchor | null;
}

/**
 * Admit a bridge: re-check the witness, spend one admission against the
 * consumption ledger, and — only on success — emit a {@link BridgeReceipt}.
 * Returns the admitted verdict together with the advanced ledger, or the
 * blocking verdict (missing / stale / consumed / …) when admission fails.
 */
export function admitBridgeWitness(
  requirement: BridgeWitnessRequirement,
  witness: BridgeWitness | null,
  request: AdmissionRequest,
  options: EvaluateWitnessOptions = {},
): {
  readonly verdict: BridgeWitnessVerdict;
  readonly consumption: WitnessConsumption | null;
} {
  const verdict = evaluateBridgeWitness(requirement, witness, options);
  if (verdict.status !== "present") {
    return {
      verdict,
      consumption:
        options.consumption !== undefined && isWitnessConsumption(options.consumption)
          ? options.consumption
          : null,
    };
  }
  // The non-feedback invariant is enforced before the witness is spent: an
  // ill-formed trace (time reversal, or B4 accounting fed back as B2) can never
  // be persisted as a receipt, and the witness is not consumed by the attempt.
  const trace = request.trace ?? SEAM_NORMAL_FORM_TRACE;
  if (!isFeedbackFreeTrace(trace) || !isSeamNormalFormTrace(trace)) {
    return {
      verdict: { status: "invalid-trace", witness: verdict.witness, trace },
      consumption: options.consumption ?? null,
    };
  }
  // Only a ledger opened against this witness governs its consumption; a ledger
  // for a different witness id is ignored and a fresh exactly-once ledger opened.
  const candidateLedger =
    options.consumption?.witnessId === verdict.witness.id ? options.consumption : null;
  if (candidateLedger !== null && !isWitnessConsumption(candidateLedger)) {
    return {
      verdict: { status: "consumed", witness: verdict.witness },
      consumption: null,
    };
  }
  const ledger = candidateLedger ?? openConsumption(verdict.witness.id, 1);
  const spent = consumeWitnessUse(ledger);
  if (!spent.admitted) {
    return {
      verdict: { status: "consumed", witness: verdict.witness },
      consumption: spent.consumption,
    };
  }
  const receipt: BridgeReceipt = {
    bridgeId: request.bridgeId,
    witnessId: verdict.witness.id,
    guarantee: verdict.witness.guarantee,
    consumedResources: request.consumedResources ?? [],
    trace,
    previousAnchor: request.previousAnchor ?? null,
    nextAnchor: request.nextAnchor ?? null,
  };
  return {
    verdict: { status: "admitted", witness: verdict.witness, receipt },
    consumption: spent.consumption,
  };
}

/** True when a verdict admitted the bridge and emitted a receipt. */
export function isAdmitted(
  verdict: BridgeWitnessVerdict,
): verdict is Extract<BridgeWitnessVerdict, { status: "admitted" }> {
  return verdict.status === "admitted";
}
